import type { AuthUser } from '@/types/auth';

/**
 * Role-check helpers for the location-tracking module.
 *
 * The OSRM path-correction endpoints (`/match-route`, `/insert-osrm-path`,
 * `/sync-osrm-data`) all require `LOCATION.TRACKING_EDITOR` — with `ADMIN`
 * treated as always-authorised. See
 * docs/location-tracking/osrm-optimization.md §"Who Can Access This Feature".
 *
 * We enforce the same client-side so a viewer never even sees the right-
 * click menu or preview actions — the server still guards on every call
 * (returns 403 otherwise) so this UI check is presentation, not security.
 */

export const TRACKING_EDITOR_ROLE = 'LOCATION.TRACKING_EDITOR';
export const ADMIN_ROLE = 'ADMIN';

/**
 * True when `user` has permission to run OSRM path corrections
 * (compute / apply / reject / sync). Missing user, missing roles array,
 * or a plain viewer/report role all return false.
 */
export function canEditTracking(user: AuthUser | null | undefined): boolean {
  const roles = user?.roles;
  if (!Array.isArray(roles) || roles.length === 0) return false;
  return roles.includes(ADMIN_ROLE) || roles.includes(TRACKING_EDITOR_ROLE);
}
